import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Gamepad2, Users, Trophy, Heart } from 'lucide-react';
import { SEO } from '@/components/SEO';

const AboutPage = () => {
  const stats = [
    { icon: Gamepad2, label: 'Games Available', value: '1000+' },
    { icon: Users, label: 'Active Players', value: '50K+' },
    { icon: Trophy, label: 'Categories', value: '20+' },
    { icon: Heart, label: 'Player Satisfaction', value: '98%' }
  ];

  const features = [
    {
      title: 'Free to Play',
      description: 'All our games are completely free with no hidden costs or subscription fees.'
    },
    {
      title: 'No Downloads Required',
      description: 'Play instantly in your browser without downloading or installing anything.'
    },
    {
      title: 'Mobile Friendly',
      description: 'Enjoy games on any device - desktop, tablet, or mobile phone.'
    },
    {
      title: 'Regular Updates',
      description: 'We add new games regularly to keep your gaming experience fresh.'
    },
    {
      title: 'Safe & Secure',
      description: 'All games are tested for safety and run in a secure environment.'
    },
    {
      title: 'Family Friendly',
      description: 'Curated content suitable for players of all ages.'
    }
  ];

  return (
    <>
      <SEO 
        pageType="about"
        title="About Us - Your Ultimate Gaming Destination"
        description="Learn about our mission to provide the best free online gaming experience. Discover our story, values, and commitment to gamers worldwide."
      />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">About Our Gaming Platform</h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                We're passionate about bringing you the best free online gaming experience. 
                Our platform hosts thousands of games across multiple categories, 
                all playable instantly in your browser.
              </p>
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card key={index} className="text-center">
                    <CardContent className="pt-6">
                      <Icon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Mission Section */}
            <Card className="mb-12">
              <CardHeader>
                <CardTitle className="text-2xl">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Our mission is to make gaming accessible to everyone, everywhere. We believe that 
                  great games should be free, instant, and available on any device. Whether you're 
                  looking for a quick puzzle to solve during your coffee break or an action-packed 
                  adventure to unwind after work, we've got you covered.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We carefully curate our game collection to ensure quality, safety, and fun. 
                  Every game is tested to work seamlessly across different browsers and devices, 
                  providing you with the best possible gaming experience.
                </p>
              </CardContent>
            </Card>

            {/* Features Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose Us?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature, index) => (
                  <Card key={index} className="h-full">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {feature.title}
                        <Badge variant="secondary">✓</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">{feature.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Team Section */}
            <Card className="mb-12">
              <CardHeader>
                <CardTitle className="text-2xl">Our Team</CardTitle>
                <CardDescription>
                  We're a dedicated team of gaming enthusiasts, developers, and designers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Our team consists of passionate gamers who understand what makes a great gaming 
                  experience. From game developers to UX designers, we work together to ensure 
                  that every aspect of our platform serves our community of players.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We're always listening to feedback from our players and constantly working 
                  to improve the platform. Your gaming experience matters to us, and we're 
                  committed to making it better every day.
                </p>
              </CardContent>
            </Card>

            {/* Community Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Join Our Community</CardTitle>
                <CardDescription>
                  Connect with fellow gamers and stay updated with the latest additions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Gaming is more fun when shared! Join our growing community of players who 
                  share tips, discuss their favorite games, and help each other discover 
                  new gaming experiences.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">Discord Community</Badge>
                  <Badge variant="outline">Weekly Tournaments</Badge>
                  <Badge variant="outline">Game Reviews</Badge>
                  <Badge variant="outline">Player Spotlights</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;
